import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Pagination from '@mui/material/Pagination';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { URLS } from '../../resources/constants';
import useDebounce from '../../resources/hooks/UseDebounce';
import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { getBranch } from '../../store/user/selectors';
import VendorFormModal, { EMPTY_VENDOR, VendorFormValues, accountLabel } from './VendorFormModal';
import GenerateFormModal from './GenerateFormModal';
import W9Modal from './W9Modal';
import VendorPaymentsModal from './VendorPaymentsModal';

/**
 * Vendor Center & 1099 vendor master: list, create, edit, soft delete,
 * QuickBooks sync, and automated RevRex expense ingestion.
 *
 * Every request is scoped by the selected branch. The scene waits for a branch
 * before fetching -- gl-api requires branch_id and would 422 without it.
 */

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const KebabToggle = React.forwardRef<HTMLButtonElement, any>(({ children, onClick, ...rest }, ref) => {
  const { t } = useTranslation('Tax1099Vendors');
  return (
  <button
    ref={ref}
    type="button"
    className="btn btn-sm d-inline-flex align-items-center justify-content-center p-0 rounded-circle"
    style={{
      width: '28px',
      height: '28px',
      background: 'transparent',
      border: 'none',
      color: '#14509e',
      verticalAlign: 'middle',
      transition: 'background-color 0.15s ease-in-out',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#edf6fc';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    }}
    aria-label={t('actions.menu')}
    title={t('actions.menu')}
    onClick={(e) => {
      e.preventDefault();
      onClick?.(e);
    }}
    {...rest}
  >
    {children || (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="#14509e" aria-hidden="true">
        <circle cx="8" cy="3" r="1.8" />
        <circle cx="8" cy="8" r="1.8" />
        <circle cx="8" cy="13" r="1.8" />
      </svg>
    )}
  </button>
  );
});

interface VendorRow {
  id: number;
  vendor_code: string;
  display_name: string;
  email?: string | null;
  phone?: string | null;
  vendor_type?: number | null;
  is_1099_eligible?: number | null;
  default_form_type?: string | null;
  default_box?: string | null;
  status: number;
  recipient_edelivery_consent: boolean;
  qbo_vendor_id?: number | null;
  qbo_id?: string | null;
  source?: string | null;
  has_w9?: boolean;
  tin_masked?: string | null;
  tin_type?: number | null;
  w9_verification_status?: number | null;
  expense_count?: number;
  ytd_gross?: number;
}

interface VendorSummary {
  total: number;
  qbo_synced: number;
  qbd_synced?: number;
  eligible: number;
  active: number;
  w9_on_file: number;
  ready: number;
  w9_needed: number;
  ytd_reportable_paid?: number;
  candidate_forms?: number;
}

// Same range the payments grid offers, so the two screens agree on which years
// are reachable.
const CURRENT_YEAR = new Date().getFullYear();
const TAX_YEARS = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const Tax1099Vendors: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('Tax1099Vendors');
  const branchData: any = useSelector(getBranch);
  const branchId = branchData?.id ?? null;

  const {
    fetchVendorList,
    fetchVendorDetails,
    createVendor,
    updateVendor,
    deleteVendor,
    generateFormForVendor,
    extractW9,
    confirmW9,
    fetchCurrentW9,
    downloadW9Pdf,
    syncQboVendors,
    syncExpenses,
    fetchNextVendorCode,
  } = useTax1099Api();

  const [rows, setRows] = useState<VendorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<VendorSummary | null>(null);
  const [loading, setLoading] = useState(false);
  // The year the list is scoped to. Everything money-shaped on this screen --
  // the gross, the threshold badge, the payment count -- is a per-return figure,
  // so it is all read through this one value. Defaults to the current year,
  // which is what the API assumed before this was selectable.
  const [taxYear, setTaxYear] = useState<number>(new Date().getFullYear());
  const [listTaxYear, setListTaxYear] = useState<number>(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);

  const [generateTarget, setGenerateTarget] = useState<VendorRow | null>(null);
  const [generating, setGenerating] = useState(false);
  const [w9Target, setW9Target] = useState<VendorRow | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState('');
  const [hasW9Filter, setHasW9Filter] = useState('');
  // Server-side equivalent of the candidate rule the KPI badges count with.
  const [candidateOnly, setCandidateOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [activeTab, setActiveTab] = useState<'all' | 'eligible' | 'w9_needed' | 'ready'>('all');
  const [paymentsTarget, setPaymentsTarget] = useState<VendorRow | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'error'>('idle');
  const hasAutoSynced = useRef(false);

  const [showForm, setShowForm] = useState(false);
  const [formInitial, setFormInitial] = useState<VendorFormValues>(EMPTY_VENDOR);

  // useDebounce here is an effect hook -- it runs the callback after the delay
  // rather than returning a debounced value.
  useDebounce(
    () => {
      const next = searchInput.trim();
      setPage(1);
      setSearch((previous) => (previous === next ? previous : next));
    },
    [searchInput],
    400,
  );

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / pageSize) : 1),
    [total, pageSize],
  );

  const loadVendors = useCallback(() => {
    if (!branchId) {
      return;
    }
    setLoading(true);

    const params: Record<string, any> = {
      branch_id: branchId,
      page,
      page_size: pageSize,
    };
    if (search) {
      params.search = search;
    }
    if (statusFilter !== '') {
      params.status = statusFilter;
    }
    // "derive from W-9" is NULL in the database and cannot be sent as an
    // integer, so it travels as its own flag.
    if (eligibilityFilter === 'unset') {
      params.eligibility_unset = true;
    } else if (eligibilityFilter !== '') {
      params.is_1099_eligible = eligibilityFilter;
    }
    if (hasW9Filter === '1') {
      params.has_w9 = true;
    } else if (hasW9Filter === '0') {
      params.has_w9 = false;
    }
    // The W-9 Needed and 1099 Ready badges count candidates -- active, not
    // exempt, over the box threshold. Asking for is_1099_eligible = 1 instead
    // listed nothing, because a QBO-synced vendor has it unset, not 1.
    if (candidateOnly) {
      params.candidate_only = true;
    }
    params.tax_year = taxYear;

    fetchVendorList(
      params,
      (message: string, response: any) => {
        // ResponseModel wraps the payload, so the axios body is
        // { success, message, data: { count, results } } -- hence data.data.
        const data = response?.data?.data ?? {};
        setRows(data.results ?? []);
        setTotal(data.count ?? 0);
        if (data.summary) {
          setSummary(data.summary);
        }
        // The row's expense count and threshold are scoped to this year. The
        // payments modal opens on the same year so the two cannot disagree.
        if (data.tax_year) {
          setListTaxYear(Number(data.tax_year));
        }
        setLoading(false);
      },
      (message: string) => {
        setLoading(false);
        toast.error(message || t('errors.load_failed'));
      },
    );
    // eslint-disable-next-line
  }, [branchId, page, pageSize, search, statusFilter, eligibilityFilter, hasW9Filter, candidateOnly, taxYear]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Automatically sync QuickBooks vendors and RevRex expenses in the background on initial menu open
  useEffect(() => {
    if (!branchId || hasAutoSynced.current) {
      return;
    }
    hasAutoSynced.current = true;
    setSyncStatus('syncing');
    syncQboVendors(
      { branch_id: branchId },
      () => {
        syncExpenses(
          { branch_id: branchId },
          () => {
            setSyncStatus('synced');
            loadVendors();
          },
          () => {
            setSyncStatus('synced');
            loadVendors();
          },
        );
      },
      () => {
        syncExpenses(
          { branch_id: branchId },
          () => {
            setSyncStatus('synced');
            loadVendors();
          },
          () => {
            setSyncStatus('error');
            loadVendors();
          },
        );
      },
    );
  }, [branchId, syncQboVendors, syncExpenses, loadVendors]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, eligibilityFilter, hasW9Filter, pageSize]);

  // The visible `rows` are only the current page (pageSize can be as low as
  // 20 against 28+ vendors), so guessing the next code from them undercounts
  // and can hand out a code that already exists on a later page. The server
  // (vendor_repo.get_next_vendor_code) sees every vendor and is the only
  // source of truth -- wait for it instead of showing a number that might
  // be wrong.
  const openCreate = () => {
    setFormInitial({ ...EMPTY_VENDOR, vendor_code: '', source: 'manual' });
    setShowForm(true);
    if (!branchId) return;

    setCodeLoading(true);
    fetchNextVendorCode(
      { branch_id: branchId },
      (_msg: string, response: any) => {
        setCodeLoading(false);
        const nextCode = response?.data?.data?.vendor_code;
        if (nextCode) {
          setFormInitial((prev: any) => ({ ...prev, vendor_code: nextCode }));
        } else {
          toast.error(t('errors.vendor_code_failed', 'Could not generate the next vendor code. Please retry.'));
        }
      },
      () => {
        setCodeLoading(false);
        toast.error(t('errors.vendor_code_failed', 'Could not generate the next vendor code. Please retry.'));
      }
    );
  };

  const openEdit = (vendorId: number) => {
    if (!branchId) {
      return;
    }
    fetchVendorDetails(
      { vendor_id: vendorId, branch_id: branchId },
      (message: string, response: any) => {
        const vendor = response?.data?.data ?? {};
        setFormInitial({
          id: vendor.id,
          vendor_code: vendor.vendor_code ?? '',
          display_name: vendor.display_name ?? '',
          contact_name: vendor.contact_name ?? '',
          email: vendor.email ?? '',
          phone: vendor.phone ?? '',
          vendor_type: vendor.vendor_type == null ? '' : String(vendor.vendor_type),
          is_1099_eligible:
            vendor.is_1099_eligible == null ? '' : String(vendor.is_1099_eligible),
          default_form_type: vendor.default_form_type ?? '',
          default_box: vendor.default_box ?? '',
          default_account_chart_id: vendor.default_account_chart_id ?? null,
          default_account_label: accountLabel(vendor.default_account_chart),
          status: String(vendor.status ?? 1),
          recipient_edelivery_consent: Boolean(vendor.recipient_edelivery_consent),
          address_line1: vendor.address_line1 ?? '',
          address_line2: vendor.address_line2 ?? '',
          city: vendor.city ?? '',
          state: vendor.state ?? '',
          postal_code: vendor.postal_code ?? '',
          qbo_id: vendor.qbo_id ?? '',
          source: vendor.source ?? (vendor.qbo_vendor_id ? 'qbo' : 'manual'),
        });
        setShowForm(true);
      },
      (message: string) => toast.error(message || t('errors.load_failed')),
    );
  };

  /** Empty strings mean "not set" in the form but must not reach the API as "". */
  const toPayload = (values: VendorFormValues) => {
    const payload: Record<string, any> = {
      branch_id: branchId,
      display_name: values.display_name.trim(),
      status: Number(values.status),
      recipient_edelivery_consent: values.recipient_edelivery_consent,
    };

    const optionalText: Array<keyof VendorFormValues> = [
      'contact_name', 'email', 'phone',
      'address_line1', 'address_line2', 'city', 'state', 'postal_code',
      'default_form_type', 'default_box',
    ];
    optionalText.forEach((field) => {
      const value = String(values[field] ?? '').trim();
      if (value) {
        payload[field] = value;
      } else if (values.id) {
        payload[field] = null;
      }
    });

    if (values.vendor_type !== '') {
      payload.vendor_type = Number(values.vendor_type);
    } else if (values.id) {
      payload.vendor_type = null;
    }

    if (values.is_1099_eligible !== '') {
      payload.is_1099_eligible = Number(values.is_1099_eligible);
    } else if (values.id) {
      payload.is_1099_eligible = null;
    }

    if (values.default_account_chart_id || values.id) {
      payload.default_account_chart_id = values.default_account_chart_id;
    }

    return payload;
  };

  const handleSubmit = (values: VendorFormValues) => {
    setSaving(true);
    const payload = toPayload(values);

    const onSuccess = (message: string) => {
      setSaving(false);
      setShowForm(false);
      toast.success(message || t('messages.saved'));
      loadVendors();
    };
    const onError = (message: string) => {
      setSaving(false);
      // 409 from a duplicate vendor_code arrives here with the API's message.
      toast.error(message || t('errors.save_failed'));
    };

    if (values.id) {
      updateVendor({ ...payload, vendor_id: values.id }, onSuccess, onError);
    } else {
      createVendor({ ...payload, vendor_code: values.vendor_code.trim() }, onSuccess, onError);
    }
  };

  const handleDelete = (row: VendorRow) => {
    if (!branchId) {
      return;
    }
    if (!window.confirm(t('confirm.delete', { name: row.display_name }))) {
      return;
    }
    deleteVendor(
      { vendor_id: row.id, branch_id: branchId },
      (message: string) => {
        toast.success(message || t('messages.deleted'));
        loadVendors();
      },
      (message: string) => toast.error(message || t('errors.delete_failed')),
    );
  };

  const handleGenerate = (taxYear: number, force: boolean) => {
    if (!branchId || !generateTarget) {
      return;
    }
    setGenerating(true);
    generateFormForVendor(
      { vendor_id: generateTarget.id, branch_id: branchId, tax_year: taxYear, force },
      (message: string, response: any) => {
        setGenerating(false);
        setGenerateTarget(null);
        const created = response?.data?.data?.created ?? [];
        toast.success(
          t('generate.success', { count: created.length, vendor: generateTarget.display_name }),
        );
      },
      (message: string) => {
        setGenerating(false);
        // gl-api raises the skip reason (below threshold, corporate exempt,
        // already exists...) as the error detail -- surface it verbatim
        // rather than a generic failure message.
        toast.error(message || t('generate.failed'));
      },
    );
  };

  const handleViewW9Pdf = (row: VendorRow) => {
    if (!branchId) return;
    downloadW9Pdf(
      { vendor_id: row.id, branch_id: branchId },
      (_message: string, blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      (err: any) => {
        toast.error(err?.response?.data?.message || t('w9.errors.no_pdf', 'No W-9 PDF available on record for this vendor'));
      },
    );
  };


  const formatCurrency = (val?: number) => {
    if (!val || val === 0) return '$0.00';
    return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // One line per QuickBooks product the vendors came from (qbo_vendors.domain,
  // copied to source by the sync). Neither synced yet: the QBO line, at zero.
  const rowsFrom = (key: string) =>
    rows.filter((r) => r.qbo_vendor_id && (r.source === 'qbd' ? 'qbd' : 'qbo') === key).length;
  const syncedCounts = [
    { key: 'qbo', count: summary ? summary.qbo_synced : rowsFrom('qbo') },
    { key: 'qbd', count: summary ? summary.qbd_synced ?? 0 : rowsFrom('qbd') },
  ].filter(({ key, count }, _i, all) => count > 0 || (key === 'qbo' && all.every((c) => !c.count)));

  const sourceBadge = (row: VendorRow) => {
    const src = (row.source || (row.qbo_vendor_id ? 'qbo' : 'manual')).toLowerCase();
    if (src === 'qbo' || src === 'qbd') {
      return (
        <span
          className="badge"
          style={{
            color: '#14509e',
            backgroundColor: '#edf6fc',
            border: '1px solid #b6d9f7',
            fontWeight: 700,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '11px',
          }}
        >
          {src === 'qbd' ? t('source_qbd', 'QuickBooks Desktop') : t('source_qbo', 'QuickBooks Online')}
        </span>
      );
    }
    if (src === 'bank_deposit') {
      return (
        <span
          className="badge"
          style={{
            color: '#6b149e',
            backgroundColor: '#f3e8fd',
            border: '1px solid #d9b6f7',
            fontWeight: 700,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '11px',
          }}
        >
          {t('source_bank_deposit', 'Bank Deposit')}
        </span>
      );
    }
    return (
      <span
        className="badge"
        style={{
          color: '#475569',
          backgroundColor: '#f1f5f9',
          border: '1px solid #cbd5e1',
          fontWeight: 700,
          padding: '4px 8px',
          borderRadius: '999px',
          fontSize: '11px',
        }}
      >
        {t('source_manual', 'Manual Entry')}
      </span>
    );
  };

  const w9Badge = (row: VendorRow) => {
    if (row.has_w9) {
      return (
        <span
          className="badge"
          style={{
            color: '#137a55',
            backgroundColor: '#eaf7f1',
            fontWeight: 700,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '11px',
          }}
        >
          ✓ {t('badges.w9_confirmed', 'W-9 Confirmed')}
        </span>
      );
    }
    const gross = Number(row.ytd_gross ?? 0);
    const threshold = (row.default_form_type === '1099-MISC' && row.default_box === 'MISC-10') ? 600 : 2000;
    if (gross < threshold) {
      return (
        <span
          className="badge"
          style={{
            color: '#5e6675',
            backgroundColor: '#eff2f6',
            fontWeight: 600,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '11px',
          }}
        >
          {t('badges.under_threshold', 'Under Threshold (No {{year}} 1099 Due)', { year: listTaxYear })}
        </span>
      );
    }
    return (
      <span
        className="badge"
        style={{
          color: '#b13c46',
          backgroundColor: '#fff0f1',
          fontWeight: 700,
          padding: '4px 8px',
          borderRadius: '999px',
          fontSize: '11px',
        }}
      >
        {t('tabs.w9_needed', 'W-9 Needed')}
      </span>
    );
  };

  const candidateFormBadge = (row: VendorRow) => {
    const formType = row.default_form_type || '1099-NEC';
    const boxCode = row.default_box ? row.default_box.replace('_', '-') : t('badges.box_1', 'Box 1');
    const isNec = formType === '1099-NEC';
    return (
      <span
        className="badge"
        style={{
          color: isNec ? '#14509e' : '#6938ef',
          backgroundColor: isNec ? '#edf6fc' : '#f4f0ff',
          fontWeight: 700,
          padding: '4px 8px',
          borderRadius: '999px',
          fontSize: '11px',
        }}
      >
        {formType} ({boxCode})
      </span>
    );
  };

  const thresholdBadge = (row: VendorRow) => {
    const gross = row.ytd_gross ?? 0;
    const threshold = (row.default_form_type === '1099-MISC' && row.default_box === 'MISC-10') ? 600 : 2000;
    if (gross >= threshold) {
      return (
        <span
          className="badge"
          style={{
            color: '#137a55',
            backgroundColor: '#eaf7f1',
            fontWeight: 700,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '11px',
          }}
        >
          {t('badges.threshold_cleared', 'Cleared (${{amount}} threshold)', { amount: threshold.toLocaleString() })}
        </span>
      );
    }
    if (gross > 0) {
      return (
        <span
          className="badge"
          style={{
            color: '#667085',
            backgroundColor: '#eff2f6',
            fontWeight: 600,
            padding: '4px 8px',
            borderRadius: '999px',
            fontSize: '11px',
          }}
        >
          {t('badges.threshold_below', 'Below ${{amount}} threshold for {{year}}', { amount: threshold.toLocaleString(), year: listTaxYear })}
        </span>
      );
    }
    return <small className="text-muted">{t('badges.no_year_payments', 'No {{year}} payments', { year: listTaxYear })}</small>;
  };

  if (!branchId) {
    return (
      <div className="p-4">
        <h4>{t('page.title')}</h4>
        <p className="text-muted">{t('messages.select_branch')}</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h4 className="mb-0">{t('page.title', 'Vendor Management')}</h4>
          <small className="text-muted">
            {t(
              'page.subtitle',
              'Contractors and {{source}}-synced vendors tracked for 1099 reporting',
              {
                source: syncedCounts
                  .map(({ key }) => key.toUpperCase())
                  .join(' or '),
              },
            )}
          </small>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {syncStatus === 'syncing' && (
            <span
              className="d-inline-flex align-items-center gap-2 px-3 py-1 text-muted"
              style={{
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: '#f1f5f9',
                borderRadius: '999px',
                border: '1px solid #e2e8f0',
              }}
            >
              <span
                className="spinner-border spinner-border-sm text-primary"
                style={{ width: '11px', height: '11px', borderWidth: '2px' }}
              />
              <span>
                {t('sync.syncing', 'Syncing with {{source}} & RevRex...', {
                  source: syncedCounts
                    .map(({ key }) => key.toUpperCase())
                    .join(' or '),
                })}
              </span>
            </span>
          )}
          {syncStatus === 'synced' && (
            <span
              className="d-inline-flex align-items-center gap-2 px-3 py-1"
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#15803d',
                backgroundColor: '#ecfdf5',
                borderRadius: '999px',
                border: '1px solid #a7f3d0',
              }}
              title={t('sync.in_sync_tooltip')}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                }}
              />
              <span>
                {t('sync.synced', 'Synced with {{source}} & RevRex', {
                  source: syncedCounts
                    .map(({ key }) => key.toUpperCase())
                    .join(' or '),
                })}
              </span>
            </span>
          )}
          {syncStatus === 'error' && (
            <span
              className="d-inline-flex align-items-center gap-2 px-3 py-1"
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#b45309',
                backgroundColor: '#fffbeb',
                borderRadius: '999px',
                border: '1px solid #fde68a',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b',
                }}
              />
              <span>{t('sync.offline', 'Sync offline · Cached')}</span>
            </span>
          )}
          <Button
            variant="outline-primary"
            style={{
              borderColor: '#14509e',
              color: '#14509e',
              backgroundColor: '#fff',
              fontWeight: 700,
            }}
            onClick={() => navigate(URLS.TAX_1099_PAYMENTS)}
          >
            📋 {t('actions.payment_ledger', 'Itemized Payment Ledger')}
          </Button>
          <Button
            style={{ backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }}
            onClick={openCreate}
          >
            + {t('actions.add_vendor', 'Add Vendor')}
          </Button>
        </div>
      </div>

      {/* KPI Metric Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card shadow-sm border h-100 p-3" style={{ borderRadius: '14px', border: '1px solid #dfe7f0' }}>
            <span className="text-muted small fw-bold text-uppercase">
              {t('metrics.total_vendors', 'Total Active Vendors')}
            </span>
            <div className="h3 fw-bold text-dark my-1" style={{ color: '#0d2c55' }}>
              {summary ? summary.active : total}
            </div>
            {syncedCounts.map(({ key, count }) => (
              <span key={key} className="small fw-semibold d-block" style={{ color: '#137a55' }}>
                ● {count} {t(`metrics.synced_from_${key}`)}
              </span>
            ))}
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card shadow-sm border h-100 p-3" style={{ borderRadius: '14px', border: '1px solid #dfe7f0' }}>
            <span className="text-muted small fw-bold text-uppercase">
              {t('metrics.w9_on_file', 'W-9 Forms on File')}
            </span>
            <div className="h3 fw-bold text-dark my-1" style={{ color: '#0d2c55' }}>
              {summary ? summary.w9_on_file : 0}
            </div>
            <span className="small text-muted">
              {summary && summary.total ? Math.round((summary.w9_on_file / summary.total) * 100) : 0}% {t('metrics.verified_signed', 'Verified & Signed')}
            </span>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card shadow-sm border h-100 p-3" style={{ borderRadius: '14px', border: '1px solid #dfe7f0' }}>
            <span className="text-muted small fw-bold text-uppercase">
              {t('metrics.ytd_reportable_paid', 'YTD Reportable Paid')}
            </span>
            <div className="h3 fw-bold my-1" style={{ color: '#0d2c55' }}>
              {formatCurrency(summary?.ytd_reportable_paid ?? 0)}
            </div>
            <span className="small text-muted">
              {t('metrics.excludes_card_payments', 'Excludes IRC § 6050W Card Pymts')}
            </span>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card shadow-sm border h-100 p-3" style={{ borderRadius: '14px', border: '1px solid #dfe7f0' }}>
            <span className="text-muted small fw-bold text-uppercase">
              {t('metrics.candidate_forms', 'Candidate 1099 Forms')}
            </span>
            <div className="h3 fw-bold my-1" style={{ color: '#0d2c55' }}>
              {summary ? (summary.candidate_forms ?? summary.eligible) : 0}
            </div>
            <span className="small fw-semibold" style={{ color: '#6938ef' }}>
              {summary ? summary.eligible : 0} {t('metrics.identified_for_1099', 'Identified for 1099')}
            </span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-3 border-bottom pb-2">
        <Button
          variant={activeTab === 'all' ? 'primary' : 'light'}
          size="sm"
          className="rounded-pill px-3"
          onClick={() => {
            setActiveTab('all');
            setEligibilityFilter('');
            setStatusFilter('');
            setHasW9Filter('');
            setCandidateOnly(false);
          }}
        >
          {t('tabs.all_vendors', 'All Vendors')} ({summary ? summary.total : total})
        </Button>
        <Button
          variant={activeTab === 'eligible' ? 'primary' : 'light'}
          size="sm"
          className="rounded-pill px-3"
          onClick={() => {
            setActiveTab('eligible');
            setEligibilityFilter('1');
            setStatusFilter('');
            setHasW9Filter('');
            setCandidateOnly(false);
          }}
        >
          {t('tabs.eligible', '1099 Eligible')} {summary ? `(${summary.eligible})` : ''}
        </Button>
        <Button
          variant={activeTab === 'w9_needed' ? 'primary' : 'light'}
          size="sm"
          className="rounded-pill px-3"
          onClick={() => {
            setActiveTab('w9_needed');
            setEligibilityFilter('');
            setStatusFilter('');
            setHasW9Filter('0');
            setCandidateOnly(true);
          }}
        >
          {t('tabs.w9_needed', 'W-9 Needed')} {summary ? `(${summary.w9_needed})` : ''}
        </Button>
        <Button
          variant={activeTab === 'ready' ? 'primary' : 'light'}
          size="sm"
          className="rounded-pill px-3"
          onClick={() => {
            setActiveTab('ready');
            setEligibilityFilter('');
            setStatusFilter('');
            setHasW9Filter('1');
            setCandidateOnly(true);
          }}
        >
          {t('tabs.ready', '1099 Ready')} {summary ? `(${summary.ready})` : ''}
        </Button>
      </div>

      <div className="row mb-3">
        <div className="col-md-3 mb-2">
          <Form.Control
            type="search"
            placeholder={t('filter.search_placeholder')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <div className="col-md-2 mb-2">
          {/* A 1099 is a per-year return, so the whole screen is scoped to one
              year. Without this a vendor paid last year read as "No Payments"
              with its Expenses button dead, and there was no way from here to
              see the year the money was actually in. */}
          <Form.Select
            value={taxYear}
            onChange={(event) => setTaxYear(Number(event.target.value))}
            aria-label={t('filter.tax_year', 'Tax year')}
          >
            {TAX_YEARS.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3 mb-2">
          <Form.Select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">{t('filter.all_statuses')}</option>
            <option value="1">{t('status.active')}</option>
            <option value="0">{t('status.inactive')}</option>
            <option value="2">{t('status.archived')}</option>
          </Form.Select>
        </div>
        <div className="col-md-3 mb-2">
          <Form.Select
            value={eligibilityFilter}
            onChange={(event) => setEligibilityFilter(event.target.value)}
          >
            <option value="">{t('filter.all_eligibility')}</option>
            <option value="1">{t('eligibility.included')}</option>
            <option value="0">{t('eligibility.excluded')}</option>
            <option value="unset">{t('eligibility.derived')}</option>
          </Form.Select>
        </div>
        <div className="col-md-1 mb-2">
          {(searchInput || statusFilter || eligibilityFilter || hasW9Filter || activeTab !== 'all') && (
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={() => {
                setSearchInput('');
                setStatusFilter('');
                setEligibilityFilter('');
                setHasW9Filter('');
                setCandidateOnly(false);
                setActiveTab('all');
              }}
              title={t('filter.clear', 'Clear filters')}
            >
              ✕
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive" style={{ minHeight: '350px', paddingBottom: '140px' }}>
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>{t('table.headers.display_name', 'Vendor / Payee')}</th>
              <th>{t('table.headers.source', 'Source')}</th>
              <th>{t('table.headers.w9_status', 'W-9 Status')}</th>
              <th>{t('table.headers.ytd_gross', 'YTD Reportable Gross')}</th>
              <th>{t('table.headers.candidate_form', 'Candidate Form')}</th>
              <th>{t('table.headers.threshold', 'Filing Threshold')}</th>
              <th className="text-end">{t('table.headers.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  {t('messages.loading')}
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  {t('messages.no_vendors')}
                </td>
              </tr>
            )}

            {!loading && rows.map((row) => {
              const tinLabel = row.tin_type === 1 ? 'EIN' : (row.tin_type === 2 ? 'SSN' : 'TIN');
              const tinPart = row.tin_masked
                ? `${tinLabel}: ${row.tin_masked}`
                : t('vendor.tin_pending', 'TIN Pending');
              const contactPart = row.email || row.phone || '';
              const subline = contactPart ? `${tinPart} · ${contactPart}` : tinPart;

              return (
                <tr key={row.id}>
                  <td>
                    <strong className="d-block text-dark">{row.display_name}</strong>
                    <small className="text-muted">
                      <span className="badge bg-light text-secondary border me-1">{row.vendor_code}</span>
                      {row.qbo_id && (
                        <span className="badge bg-light text-muted border me-1" title={t('vendor.qbo_id_tooltip')}>
                          QBO #{row.qbo_id}
                        </span>
                      )}
                      {subline}
                    </small>
                  </td>
                  <td>{sourceBadge(row)}</td>
                  <td>{w9Badge(row)}</td>
                  <td>
                    <strong className="text-dark">{formatCurrency(row.ytd_gross)}</strong>
                  </td>
                  <td>{candidateFormBadge(row)}</td>
                  <td>{thresholdBadge(row)}</td>
                  <td className="text-end text-nowrap">
                    {/* A disabled button on its own is a dead end: it is correct
                        that there is nothing to open for this year, but it says
                        nothing about why, or that another year may have it. The
                        title carries that, and a disabled control does not fire
                        hover events in every browser, so it sits on a wrapper. */}
                    <span
                      className="d-inline-block me-2"
                      title={row.expense_count
                        ? t('vendor.payments_in_year', '{{count}} payment(s) in {{year}}', { count: row.expense_count, year: listTaxYear })
                        : t('vendor.no_payments_in_year', 'No payments in {{year}} — try another tax year', { year: listTaxYear })}
                    >
                      <Button
                        variant={row.expense_count ? "outline-primary" : "outline-secondary"}
                        size="sm"
                        className="py-0 px-2"
                        disabled={!row.expense_count}
                        onClick={() => setPaymentsTarget(row)}
                        style={!row.expense_count ? { pointerEvents: 'none' } : undefined}
                      >
                        {t('actions.expenses', 'Expenses')}{row.expense_count ? ` (${row.expense_count})` : ''}
                      </Button>
                    </span>

                    <Dropdown className="d-inline-block" align="end">
                      <Dropdown.Toggle as={KebabToggle} id={`vendor-actions-${row.id}`} />
                      <Dropdown.Menu className="shadow-sm border">
                        <Dropdown.Item onClick={() => openEdit(row.id)}>
                          {t('actions.edit')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setW9Target(row)}>
                          {t('actions.w9')}{row.has_w9 ? ' ✓' : ''}
                        </Dropdown.Item>
                        {row.has_w9 && (
                          <Dropdown.Item onClick={() => handleViewW9Pdf(row)}>
                            <i className="fa fa-file-pdf-o me-2 text-danger" />
                            {t('actions.view_w9_pdf', 'View Signed W-9 PDF')}
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={() => setGenerateTarget(row)}>
                          {t('actions.generate_1099')}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => handleDelete(row)}
                        >
                          {t('actions.delete')}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">{t('pagination.rows_per_page')}</span>
          <Form.Select
            size="sm"
            style={{ width: 90 }}
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Form.Select>
          <span className="text-muted small">
            {t('pagination.total', { count: total })}
          </span>
        </div>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_event, value) => setPage(value)}
          size="small"
        />
      </div>

      <VendorFormModal
        show={showForm}
        saving={saving}
        codeLoading={codeLoading}
        initial={formInitial}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmit}
      />

      <GenerateFormModal
        show={Boolean(generateTarget)}
        saving={generating}
        vendorName={generateTarget?.display_name ?? ''}
        onClose={() => setGenerateTarget(null)}
        onGenerate={handleGenerate}
      />

      {w9Target && branchId && (
        <W9Modal
          show={Boolean(w9Target)}
          vendorId={w9Target.id}
          vendorName={w9Target.display_name}
          branchId={branchId}
          onClose={() => setW9Target(null)}
          extractW9={extractW9}
          confirmW9={confirmW9}
          fetchCurrentW9={fetchCurrentW9}
          downloadW9Pdf={downloadW9Pdf}
          onConfirmed={() => {
            toast.success(t('w9.confirmed'));
            loadVendors();
          }}
        />
      )}

      {paymentsTarget && branchId && (
        <VendorPaymentsModal
          show={Boolean(paymentsTarget)}
          vendorId={paymentsTarget.id}
          vendorName={paymentsTarget.display_name}
          vendorCode={paymentsTarget.vendor_code}
          branchId={branchId}
          taxYear={listTaxYear}
          onClose={() => setPaymentsTarget(null)}
        />
      )}
    </div>
  );
};

export default Tax1099Vendors;
